import { ElementRef } from "@angular/core";
import {
    CellHighlight,
    Client,
    ConnectionHandler,
    EdgeHandler,
    EdgeStyleRegistry,
    getDefaultPlugins,
    Graph,
    GraphDataModel,
    GraphPluginConstructor,
    HandleConfig,
    InternalEvent,
    RubberBandHandler,
    VertexHandlerConfig,
    constants,
    TooltipHandler
} from "@maxgraph/core";
import { DEFAULT_MAX_GRAPH_CONFIG, MaxGraphConfig } from "./max-graph.models";
import {
    BLACK_COLOR,
    PRIMARY_PRESSED_COLOR,
    TRANSPARENT_COLOR
} from "@nms-ng2/app/shared/constants/colors";
import { CustomCellRenderer } from "../layout/custom-cell-renderer";
import { CustomCell } from "../models/diagram.models";
import { MaxGraphEventBus } from "./max-graph-event-bus";
import { MaxGraphHoverEventService } from "./max-graph-hover-events.service";

/**
 * Classe responsável por configurar o MaxGraph
 */
export class MaxGraphSetup {
    private readonly IMAGE_BASE_PATH: string = "./images";

    private config: MaxGraphConfig;            // NOSONAR
    private plugins: GraphPluginConstructor[]; // NOSONAR
    private readonly maxGraphHoverEventService: MaxGraphHoverEventService;
    private tooltipHandler: TooltipHandler;

    constructor(config: Partial<MaxGraphConfig>) {
        this.config = { ...DEFAULT_MAX_GRAPH_CONFIG, ...config };
        this.plugins = getDefaultPlugins();
        this.maxGraphHoverEventService = new MaxGraphHoverEventService();
    }

    /**
     * Configura o componente do MaxGraph
     *
     * @param container elemento HTML que servirá de container para o gráfico
     * @returns o gráfico
     */
    setup(container: ElementRef, maxGraphEventBus: MaxGraphEventBus): Graph {
        const graph = this.createGraph(container);
        graph.cellRenderer = new CustomCellRenderer();

        this.configureContainer(container);
        this.configureBasicSetup(graph);
        this.configureEdgeHandling();
        this.configureVertexStyle(graph);
        this.configureEdgeStyle(graph);
        this.configureHandlerStyle();
        this.configureOtherProperties();
        this.configurePlugins(graph);
        this.configureTooltip(graph);
        this.configureMouseEvents(graph, maxGraphEventBus);

        return graph;
    }

    destroy() {
        this.tooltipHandler.hide();
        this.tooltipHandler = null;
    }

    private createGraph(container: ElementRef) {
        const model: GraphDataModel = new GraphDataModel();
        return new Graph(container.nativeElement, model, this.plugins);
    }

    /**
     * Desabilita o menu de contexto.
     *
     * @param container - container do gráfico.
     */
    private configureContainer(container: ElementRef) {
        if (!this.config.contextMenu) {
            InternalEvent.disableContextMenu(container.nativeElement);
        }
    }

    /**
     * Seta as configurações básicas do gráfico.
     * - setImageBasePath: determina o path das imagens.
     * - setConnectable: habilita/desabilita a possibilidade de novas conexões.
     * - setEnabled: habilita/desabilita interações.
     * - setTooltips: define se os tooltips devem estar habilitados.
     * - setToolsetPanningtips: define se a função panorâmica deve estar habilitada.
     * - centerZoom: Especifica se as operações de zoom devem ser realizadas no centro.
     *
     * @param graph - Gráfico
     */
    private configureBasicSetup(graph: Graph) {
        Client.setImageBasePath(this.IMAGE_BASE_PATH);

        graph.setConnectable(this.config.connectable);
        graph.setEnabled(this.config.enabled);
        graph.setTooltips(this.config.tooltips);
        graph.setPanning(this.config.panning);

        graph.centerZoom = this.config.centerZoom;
    }

    private configureEdgeHandling() {
        // Necessário para permitir a correta configuração do posicionamento dos links
        const parallelEdgeFunction = () => { }; // NOSONAR
        EdgeStyleRegistry.add("CustomParallelEdgeStyle", parallelEdgeFunction, { handlerKind: "elbow", isOrthogonal: true });
    }

    private configureVertexStyle(graph: Graph) {
        const vertexStyle = graph.getStylesheet().getDefaultVertexStyle();

        vertexStyle.strokeOpacity = 0;
        vertexStyle.fontStyle = constants.FONT_STYLE_MASK.BOLD;
        vertexStyle.fillColor = TRANSPARENT_COLOR;
        vertexStyle.fontColor = BLACK_COLOR;
        vertexStyle.fontFamily = "Open Sans";
        vertexStyle.fontSize = 12;
    }

    private configureEdgeStyle(graph: Graph) {
        const edgeStyle = graph.getStylesheet().getDefaultEdgeStyle();

        edgeStyle.edgeStyle = "CustomParallelEdgeStyle";
        edgeStyle.endArrow = "none";
        edgeStyle.dashed = true;
        edgeStyle.dashPattern = "1";
        edgeStyle.strokeWidth = 2;
        edgeStyle.strokeColor = BLACK_COLOR;
        edgeStyle.verticalLabelPosition = "bottom";
        edgeStyle.verticalAlign = "bottom";
        edgeStyle.fontSize = 12;
        edgeStyle.fontFamily = "Open Sans";
        edgeStyle.fontStyle = constants.FONT_STYLE_MASK.BOLD;
    }

    private configureHandlerStyle() {
        // TODO[US-32342] - Definir o comportamento/cores.
        HandleConfig.fillColor = PRIMARY_PRESSED_COLOR;
        HandleConfig.strokeColor = PRIMARY_PRESSED_COLOR;
        VertexHandlerConfig.selectionColor = PRIMARY_PRESSED_COLOR;
    }

    /**
     * Configura os plugins do gráfico.
     * - RubberBandHandler: Manipulador de eventos que seleciona regiões retangulares.
     * - TooltipHandler: Manipulador de eventos que exibe tooltips.
     *
     * @param graph - gráfico.
     */
    private configurePlugins(graph: Graph) {
        if (this.config.rubberBand) {
            this.plugins.push(RubberBandHandler);
        }

        this.tooltipHandler = graph.getPlugin<TooltipHandler>("TooltipHandler");
        this.tooltipHandler.delay = this.config.tooltipDelay;
    }

    private configureOtherProperties() {
        ConnectionHandler.prototype.outlineConnect = true;
        EdgeHandler.prototype.manageLabelHandle = true;
        EdgeHandler.prototype.outlineConnect = true;
        CellHighlight.prototype.keepOnTop = true;
    }

    /**
     * Sobrescreve o método que constrói os tooltips das células.
     *
     * @param graph - gráfico.
     */
    private configureTooltip(graph: Graph) {
        graph.getTooltipForCell = (cell: CustomCell) => {
            if (cell.getCustomTooltip) {
                return cell.getCustomTooltip(cell);
            }

            return cell.getValue();
        };
    }

    /**
     * Configura os listeners de eventos de mouse.
     *
     * @param graph - gráfico.
     */
    private configureMouseEvents(graph: Graph, maxGraphEventBus) {
        let lastCell: CustomCell | null = null;

        graph.addMouseListener(
            {
                mouseDown(sender, evt) { },
                mouseMove: async (sender, evt) => {
                    const cell = evt.getCell() as CustomCell;

                    if (cell.popoverData || lastCell?.popoverData) {
                        if (lastCell && lastCell !== cell) {
                            this.maxGraphHoverEventService.onLeave(graph, lastCell, maxGraphEventBus);
                        }

                        if (cell && cell !== lastCell) {
                            this.maxGraphHoverEventService.onEnter(graph, cell, maxGraphEventBus);
                        }

                        lastCell = cell;
                    }
                },
                mouseUp(sender, evt) { }
            }
        );
    }
}
